import { assign, createMachine } from 'xstate';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import {
  ConsumedHandoffSession,
  HandoffCredentialsAction,
  HandoffSession,
} from '../../../models/handoffSession';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { Credentials, CredentialsEntity } from '../../../models/credentials';
import {
  HandoffSessionService,
  handoffSessionService as handoffSessionServiceImport,
} from '../../services/handoffSessionService';
import {
  CredentialsService,
  credentialsService as credentialsServiceImport,
} from '../../services/credentialsService';
import { ERR_USER_CANCELLED } from '../../../utilities/error';
import { CreateUrlHandoffSessionRequest } from '../../../lib-api/services/HandoffSession';
import { routeBuilder } from '../../../routes/routeBuilder';
import { USER_ACCESS_TOKEN_UNDEFINED_ERROR } from '../handoffCreatorMachine';
import { ProviderEntity } from '../../../models/provider';
import { POLL_INTERVAL } from '../utils/utils';
import { noop } from '../../../utilities/noop';
import { Typegen0 } from './handoffCreatorMachine.typegen';

export type HandoffCreatorMachineContext = {
  provider: ProviderEntity;
  userHintId: string | undefined;
  accessToken: string | undefined;
  handoffSession?: HandoffSession;
  handoffQRCode?: string;
  credentials?: CredentialsEntity;
  error?: ApplicationError;
};

export type HandoffCreatorMachineEvents = { type: 'EVENT_CANCEL_CONSUMED_HANDOFF_SESSION' };

export type HandoffCreatorMachineServices = {
  getCredentials: { data: CredentialsEntity };
  getHandoffSession: { data: HandoffSession };
  createUrlHandoffSession: { data: HandoffSession };
};

export type HandoffCreatorMachineProps = {
  initialContext: HandoffCreatorMachineContext;
  handoffCredentialsAction: HandoffCredentialsAction;
  version: string;
  origin: string;
  onDone: (credentials: CredentialsEntity) => void;
  onHandoffUpdated?: (handoffSession: ConsumedHandoffSession) => void;
  createUrlHandoffSessionRequest: CreateUrlHandoffSessionRequest;
};

export type HandoffCreatorMachineTestOverrides = {
  initialState?: Exclude<Typegen0['matchesStates'], object>;
  handoffSessionService?: HandoffSessionService;
  credentialsService?: CredentialsService;
  pollInterval?: number;
};

export const INVALID_HANDOFF_ACTION_ERROR = {
  errorStatus: ErrorStatus.AUTHENTICATION_ERROR,
  errorReason: ErrorReason.UNEXPECTED_HANDOFF_ERROR,
};

export const createHandoffCreatorMachine = ({
  handoffCredentialsAction,
  origin,
  version,
  initialContext,
  initialState = 'CHECK_INITIAL_PARAMS',
  onDone,
  onHandoffUpdated = noop,
  createUrlHandoffSessionRequest,
  handoffSessionService = handoffSessionServiceImport,
  credentialsService = credentialsServiceImport,
  pollInterval = POLL_INTERVAL,
}: HandoffCreatorMachineProps & HandoffCreatorMachineTestOverrides) =>
  createMachine(
    {
      id: 'HandoffCreatorMachine',
      initial: initialState,
      context: initialContext,
      tsTypes: {} as import('./handoffCreatorMachine.typegen').Typegen0,
      schema: {
        events: {} as HandoffCreatorMachineEvents,
        services: {} as HandoffCreatorMachineServices,
        context: {} as HandoffCreatorMachineContext,
      },
      predictableActionArguments: true,
      preserveActionOrder: true,
      states: {
        CHECK_INITIAL_PARAMS: {
          always: [
            {
              target: 'APPLICATION_ERROR_VIEW',
              cond: 'check_if_user_access_token_missing',
              actions: 'throw_user_access_token_error',
            },
            {
              target: 'APPLICATION_ERROR_VIEW',
              cond: 'check_if_handoff_action_is_none',
              actions: 'throw_invalid_handoff_action_error',
            },
            {
              target: 'CREATE_HANDOFF_SESSION_URL',
            },
          ],
        },
        CREATE_HANDOFF_SESSION_URL: {
          invoke: {
            src: 'createUrlHandoffSession',
            onDone: [
              {
                target: 'CHOOSE_HANDOFF_ACTION',
                actions: ['set_handoff_session_in_context', 'set_handoff_qrcode_in_context'],
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR_VIEW',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        APPLICATION_ERROR_VIEW: {
          type: 'final',
        },
        CHOOSE_HANDOFF_ACTION: {
          always: [
            {
              target: 'POLL_HANDOFF_SESSION_BANK_ID_VIEW',
              cond: 'check_if_handoff_action_bank_id',
            },
            {
              target: 'POLL_HANDOFF_SESSION_QR_CODE_VIEW',
            },
          ],
        },
        POLL_HANDOFF_SESSION_BANK_ID_VIEW: {
          initial: 'GET_HANDOFF_SESSION',
          states: {
            GET_HANDOFF_SESSION: {
              invoke: {
                src: 'getHandoffSession',
                onDone: [
                  {
                    target: 'AWAIT',
                    actions: 'set_handoff_session_in_context',
                  },
                ],
                onError: [
                  {
                    target: '#HandoffCreatorMachine.APPLICATION_ERROR_VIEW',
                    actions: 'set_error_in_context',
                  },
                ],
              },
            },
            AWAIT: {
              after: {
                POLL_INTERVAL: {
                  target:
                    '#HandoffCreatorMachine.POLL_HANDOFF_SESSION_BANK_ID_VIEW.GET_HANDOFF_SESSION',
                },
              },
            },
          },
          always: {
            target: 'POLLING_HANDOFF_CREDENTIALS_VIEW',
            cond: 'check_if_handoff_session_consumed',
            actions: 'execute_on_handoff_updated',
          },
        },
        POLL_HANDOFF_SESSION_QR_CODE_VIEW: {
          initial: 'GET_HANDOFF_SESSION',
          states: {
            GET_HANDOFF_SESSION: {
              invoke: {
                src: 'getHandoffSession',
                onDone: [
                  {
                    target: 'AWAIT',
                    actions: 'set_handoff_session_in_context',
                  },
                ],
                onError: [
                  {
                    target: '#HandoffCreatorMachine.APPLICATION_ERROR_VIEW',
                    actions: 'set_error_in_context',
                  },
                ],
              },
            },
            AWAIT: {
              after: {
                POLL_INTERVAL: {
                  target:
                    '#HandoffCreatorMachine.POLL_HANDOFF_SESSION_QR_CODE_VIEW.GET_HANDOFF_SESSION',
                },
              },
            },
          },
          always: {
            target: 'POLLING_HANDOFF_CREDENTIALS_VIEW',
            cond: 'check_if_handoff_session_consumed_and_credentials_id_exist',
            actions: 'execute_on_handoff_updated',
          },
        },
        POLLING_HANDOFF_CREDENTIALS_VIEW: {
          initial: 'GET_CREDENTIALS',
          states: {
            GET_CREDENTIALS: {
              invoke: {
                src: 'getCredentials',
                onDone: [
                  {
                    target: 'AWAIT',
                    actions: 'set_credentials_in_context',
                  },
                ],
                onError: [
                  {
                    target: '#HandoffCreatorMachine.APPLICATION_ERROR_VIEW',
                    actions: 'set_error_in_context',
                  },
                ],
              },
            },
            AWAIT: {
              after: {
                POLL_INTERVAL: {
                  target: '#HandoffCreatorMachine.POLLING_HANDOFF_CREDENTIALS_VIEW.GET_CREDENTIALS',
                },
              },
            },
          },
          always: [
            {
              target: 'DONE',
              cond: 'check_if_credentials_completed',
            },
          ],
          on: {
            EVENT_CANCEL_CONSUMED_HANDOFF_SESSION: {
              target: 'APPLICATION_ERROR_VIEW',
              actions: 'throw_user_cancelled_error',
            },
          },
        },
        DONE: {
          entry: 'execute_on_done',
          type: 'final',
        },
      },
    },
    {
      guards: {
        check_if_user_access_token_missing: ({ accessToken }) => !accessToken,
        check_if_handoff_action_is_none: () =>
          handoffCredentialsAction === HandoffCredentialsAction.NONE,
        check_if_handoff_action_bank_id: () =>
          handoffCredentialsAction === HandoffCredentialsAction.BANKID_AUTH,
        check_if_handoff_session_consumed: ({ handoffSession }) => !!handoffSession?.isConsumed,
        check_if_handoff_session_consumed_and_credentials_id_exist: ({ handoffSession }) =>
          !!handoffSession?.credentialsId && handoffSession.isConsumed,
        check_if_credentials_completed: ({ credentials }) =>
          Credentials.isUpdated(credentials) ||
          Credentials.isUpdating(credentials) ||
          Credentials.hasError(credentials),
      },
      actions: {
        set_handoff_session_in_context: assign({ handoffSession: (_, event) => event.data }),
        set_handoff_qrcode_in_context: assign({
          handoffQRCode: (_, event) =>
            routeBuilder.buildShortHandoffUrlForMobile.build({
              origin,
              version,
              handoffSessionId: event.data.id,
            }),
        }),
        set_credentials_in_context: assign({ credentials: (_, event) => event.data }),
        set_error_in_context: assign({ error: (_, event) => event.data as ApplicationError }),
        throw_invalid_handoff_action_error: assign({
          error: (_) => new ApplicationError(INVALID_HANDOFF_ACTION_ERROR),
        }),
        throw_user_access_token_error: assign({
          error: (_) => new ApplicationError(USER_ACCESS_TOKEN_UNDEFINED_ERROR),
        }),
        throw_user_cancelled_error: assign({
          error: (_) => new ApplicationError(ERR_USER_CANCELLED),
        }),
        execute_on_done: ({ credentials }) => onDone(credentials!),
        execute_on_handoff_updated: ({ handoffSession, accessToken }) => {
          if (handoffSession && onHandoffUpdated && accessToken) {
            onHandoffUpdated({ ...handoffSession, userAccessToken: accessToken });
          }
        },
      },
      services: {
        getCredentials: ({ handoffSession }) =>
          credentialsService.getCredentials(handoffSession!.credentialsId!),
        getHandoffSession: ({ handoffSession }) =>
          handoffSessionService.getHandoffSession(handoffSession!.id),
        createUrlHandoffSession: () =>
          handoffSessionService.createUrlHandoffSession(createUrlHandoffSessionRequest),
      },
      delays: { POLL_INTERVAL: pollInterval },
    }
  );
